import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import useSuscripcion from 'utils/hooks/custom/useSuscripcion'

const SuscripcionContext = createContext({
    suscripcion: null,
    plan: null,
    cuota: null,
    cargando: false,
    tienePagoPendiente: false,
    recargar: () => {},
})

export function SuscripcionProvider({ children }) {
    const { token, signedIn } = useSelector((state) => state.auth.session)
    const user = useSelector((state) => state.auth.user)
    const suscripcionApi = useSuscripcion()

    const [suscripcion, setSuscripcion] = useState(null)
    const [plan, setPlan] = useState(null)
    const [cuota, setCuota] = useState(null)
    const [cargando, setCargando] = useState(false)

    const cargar = useCallback(async () => {
        if (!signedIn || !token || !user?.empresa) {
            setSuscripcion(null)
            setPlan(null)
            setCuota(null)
            return
        }

        try {
            setCargando(true)
            const resp = await suscripcionApi.mia()
            if (resp?.data) {
                setSuscripcion(resp.data.suscripcion || null)
                setPlan(resp.data.plan || null)
                setCuota(resp.data.cuota || null)
            }
        } catch (e) {
            // Si falla la petición (p.ej. sin red), no rompemos la UI.
        } finally {
            setCargando(false)
        }
    }, [signedIn, token, user?.empresa, suscripcionApi])

    useEffect(() => {
        if (signedIn && token && user?.empresa) {
            cargar()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signedIn, token, user?.empresa])

    // Determina si hay un pago pendiente para el plan seleccionado.
    // Ocurre cuando:
    // - El estado es 'pendiente_pago', 'incompleta', 'past_due' o 'inactiva'
    // - O tiene un plan configurado de pago (distinto de gratis) pero aún no tiene stripeSubscriptionId
    const tienePagoPendiente = Boolean(
        suscripcion &&
        (
            suscripcion.estado === 'pendiente_pago' ||
            suscripcion.estado === 'incompleta' ||
            suscripcion.estado === 'past_due' ||
            suscripcion.estado === 'inactiva' ||
            (!suscripcion.stripeSubscriptionId && suscripcion.plan && suscripcion.plan.codigo !== 'gratis')
        )
    )

    return (
        <SuscripcionContext.Provider
            value={{
                suscripcion,
                plan: plan || (suscripcion ? suscripcion.plan : null),
                cuota,
                cargando,
                tienePagoPendiente,
                recargar: cargar,
            }}
        >
            {children}
        </SuscripcionContext.Provider>
    )
}

export function useSuscripcionStatus() {
    return useContext(SuscripcionContext)
}

export default SuscripcionContext
